import {
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  type ActionCodeSettings,
  type Auth,
} from 'firebase/auth';

/** Persisted before sending the link so the same browser can complete sign-in. */
export const EMAIL_LINK_STORAGE_KEY = 'molish_dashboard_email_for_sign_in';

let emailLinkSignInFlight: Promise<void> | null = null;

/**
 * URL opened after the user taps the sign-in link. Must be allowlisted in Firebase Console
 * (Authentication → Settings → Authorized domains) and match the Email link domain settings.
 * Override with `VITE_FIREBASE_EMAIL_LINK_CONTINUE_URL` when production hosting URL differs from `window.location.origin`.
 */
export function getEmailLinkContinueUrl(): string {
  const fromEnv = import.meta.env.VITE_FIREBASE_EMAIL_LINK_CONTINUE_URL as string | undefined;
  if (fromEnv && fromEnv.trim().length > 0) {
    return fromEnv.trim();
  }
  return `${window.location.origin}/login`;
}

export function getEmailLinkActionCodeSettings(): ActionCodeSettings {
  return {
    url: getEmailLinkContinueUrl(),
    handleCodeInApp: true,
  };
}

export function isDashboardEmailSignInLink(authInstance: Auth, href: string): boolean {
  return isSignInWithEmailLink(authInstance, href);
}

/**
 * Completes email-link sign-in. Coalesces concurrent calls (e.g. React Strict Mode) into one `signInWithEmailLink`.
 */
export async function signInWithEmailLinkOnce(
  authInstance: Auth,
  email: string,
  href: string
): Promise<void> {
  if (emailLinkSignInFlight) {
    await emailLinkSignInFlight;
    return;
  }
  emailLinkSignInFlight = (async () => {
    try {
      await signInWithEmailLink(authInstance, email, href);
    } finally {
      emailLinkSignInFlight = null;
    }
  })();
  await emailLinkSignInFlight;
}

export async function sendDashboardEmailSignInLink(
  authInstance: Auth,
  email: string
): Promise<void> {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed) {
    throw new Error('Email required');
  }
  window.localStorage.setItem(EMAIL_LINK_STORAGE_KEY, trimmed);
  await sendSignInLinkToEmail(authInstance, trimmed, getEmailLinkActionCodeSettings());
}

export function clearStoredEmailForSignIn(): void {
  window.localStorage.removeItem(EMAIL_LINK_STORAGE_KEY);
}

export function readStoredEmailForSignIn(): string {
  return window.localStorage.getItem(EMAIL_LINK_STORAGE_KEY)?.trim().toLowerCase() ?? '';
}

/** Removes link query parameters while keeping the current path (e.g. `/login`). */
export function replaceUrlAfterEmailLinkSignIn(): void {
  window.history.replaceState(
    null,
    document.title,
    `${window.location.pathname}${window.location.hash}`
  );
}
